/* ═══════════════════════════════════════════════════════════════════════════
   Medité popup-discount bridge  (Job 2)

   The Klaviyo popup shows a STATIC coupon code that a shopper would normally have
   to copy and paste at checkout - it never touches the cart session, so nothing
   discounted shows in cart/PDP prices. This module closes that gap:

     1. When someone completes the Klaviyo signup form, it attaches the code to the
        Shopify cart session (GET /discount/CODE) so real cart prices reflect it.
     2. It remembers the code locally and exposes window.MediteDiscount.get() ->
        { code, pct } | null, which the PDP buy-box + sticky bars read to show the
        discounted price with the original struck through. Fires
        'medite:discount-changed' whenever the state changes.
     3. It self-heals: if the flag is set but a non-empty cart no longer carries the
        code (expired / removed), it clears the flag so nothing phantom ever shows.

   Config is injected from theme.liquid via window.MEDITE_POPUP_DISCOUNT = { code, pct }.
   With no config (or pct <= 0) this module is inert.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var CFG  = window.MEDITE_POPUP_DISCOUNT || {};
  var CODE = (CFG.code || '').trim();
  var PCT  = parseFloat(CFG.pct) || 0;
  if (!CODE || PCT <= 0) return;

  var LS_KEY = 'mediteDiscount';

  function read() {
    try {
      var o = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      return (o && o.code && o.pct) ? o : null;
    } catch (e) { return null; }
  }
  function write(o) { try { localStorage.setItem(LS_KEY, JSON.stringify(o)); } catch (e) {} }
  function clear()  { try { localStorage.removeItem(LS_KEY); } catch (e) {} }
  function fire()   { document.dispatchEvent(new CustomEvent('medite:discount-changed')); }

  /* verified === the code is ACTUALLY on the current cart session (ground truth from
     /cart.js). The stored localStorage flag alone is NOT enough to show a discounted
     price - a stale flag from a past visit would otherwise make the coupon price show
     even when the shopper never engaged the popup. get() below returns a discount only
     when verified is true, so nothing phantom ever renders. */
  var verified = false;
  function setVerified(v) { v = !!v; if (v !== verified) { verified = v; fire(); } }

  /* Attach the code to the cart session. redirect:'manual' means we hit the
     endpoint (server attaches the code to this cart) without actually navigating. */
  function applyToSession() {
    return fetch('/discount/' + encodeURIComponent(CODE), { method: 'GET', redirect: 'manual' })
      .catch(function () { /* opaque redirect still processed server-side */ });
  }

  var api = {
    code: CODE,
    pct:  PCT,
    /* Only returns a discount when the code is ACTUALLY on the current cart
       (verified). Falls back to the configured code/pct if there's no stored flag,
       so the display stays in lockstep with the coupon being genuinely applied -
       never off a stale flag alone. */
    get: function () { return verified ? (read() || { code: CODE, pct: PCT }) : null; },
    /* Raw stored flag regardless of verification - for internal/debug use only. */
    getRaw: function () { return read(); },
    apply: function () {
      return applyToSession().then(function () {
        write({ code: CODE, pct: PCT, ts: Date.now() });
        fire();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      });
    },
    clear: function () { clear(); fire(); }
  };
  window.MediteDiscount = api;

  /* ── Klaviyo form success → auto-apply ──
     Klaviyo dispatches a 'klaviyoForms' event on window; detail.type === 'submit'
     (or 'stepSubmit') marks a successful email submission. */
  window.addEventListener('klaviyoForms', function (e) {
    var t = e && e.detail && e.detail.type;
    if (t !== 'submit' && t !== 'stepSubmit') return;
    var cur = read();
    if (!cur || cur.code !== CODE) api.apply();
  });

  function cartHasCode(cart) {
    return !!(cart && (cart.cart_level_discount_applications || []).some(function (a) {
      return a.type === 'discount_code';
    }));
  }

  /* ── Verify against real cart state ──
     Sets `verified` from ground truth. If a flag is queued but a NON-empty cart
     doesn't carry the code, try to re-apply once; if it still won't take, clear the
     flag so nothing phantom is left behind. An empty cart (or no flag) means the
     coupon is not in use right now, so verified stays false and no discount shows. */
  function verifyFromCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cartHasCode(cart)) { setVerified(true); return; }
        if (read() && cart && cart.item_count) {
          return applyToSession()
            .then(function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); })
            .then(function (cart2) {
              if (cartHasCode(cart2)) { setVerified(true); }
              else { clear(); setVerified(false); }   // invalid/expired -> no phantom
            })
            .catch(function () { setVerified(false); });
        }
        setVerified(false);
      })
      .catch(function () {});
  }

  /* Re-verify whenever the cart changes so the discounted display appears/disappears
     in lockstep with the coupon actually being on the cart. */
  document.addEventListener('cart:refresh', verifyFromCart);
  document.addEventListener('cart:item-added', verifyFromCart);
  document.addEventListener('cart:change', verifyFromCart);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', verifyFromCart);
  } else {
    verifyFromCart();
  }
})();

/* ═══════════════════════════════════════════════════════════════════════════
   Klaviyo scroll-lock safety net
   The Klaviyo popup adds `klaviyo-prevent-body-scrolling` (overflow:hidden) to
   <body> while it is open, but occasionally fails to remove it after the popup
   is dismissed - which freezes page scroll with no popup visible. This releases
   the lock, but ONLY when the popup is not open, so it never lets the background
   scroll behind an active popup. It touches nothing but that one body class.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var KL = 'klaviyo-prevent-body-scrolling';

  function releaseKlaviyo() { document.body.classList.remove(KL); }
  function clearOpenCC() {
    var b = document.body;
    if (b.classList.contains('open-cart') || b.classList.contains('open-cc')) {
      b.classList.remove('open-cart', 'open-cc');
    }
  }
  function cartOpen() {
    var cd = document.getElementById('Cart-Drawer');
    return !!(cd && cd.classList.contains('active'));
  }

  /* Is a Klaviyo popup ACTUALLY rendered on screen right now? Checks real geometry +
     opacity of the popup form itself - NOT elementFromPoint, which returns Klaviyo's
     invisible leftover overlay during/after dismissal and would falsely report a popup
     that isn't there (and so wrongly keep the page locked). A popup only counts if its
     form is a real, opaque, on-screen box. */
  function popupVisible() {
    try {
      var els = document.querySelectorAll('form.klaviyo-form, div[data-testid="POPUP"], [class*="kl-private-reset-css"] form, [class*="kl-private-reset-css"] [role="dialog"]');
      for (var i = 0; i < els.length; i++) {
        var f = els[i], r = f.getBoundingClientRect(), st = getComputedStyle(f);
        if (r.width >= 80 && r.height >= 80 &&
            st.display !== 'none' && st.visibility !== 'hidden' && parseFloat(st.opacity || '1') > 0.1 &&
            r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth) {
          return true;
        }
      }
    } catch (e) {}
    return false;
  }

  /* The one hard guarantee the page can never stay frozen.
       force=true  → explicit user intent (scroll gesture, scroll key) or a drawer/popup
                     close: clear the lock whenever the cart drawer isn't open. We do NOT
                     gate this on popup detection - if the user is trying to scroll, they
                     get to scroll. A live popup losing its background lock is a minor,
                     rare cosmetic cost; a frozen page is not acceptable.
       force=false → passive watchdog: clear only when no popup is actually visible, with
                     a hard failsafe so even a mis-detected lock can never persist >2s. */
  var _lockedSince = 0;
  function clearLocks(force) {
    var b = document.body;
    if (cartOpen()) { _lockedSince = 0; return; }   // drawer genuinely open - leave its scroll alone
    if (!b.classList.contains(KL)) { _lockedSince = 0; clearOpenCC(); return; }
    if (force) { releaseKlaviyo(); clearOpenCC(); _lockedSince = 0; return; }
    if (!_lockedSince) _lockedSince = Date.now();
    if (!popupVisible() || (Date.now() - _lockedSince) > 2000) {
      releaseKlaviyo(); clearOpenCC(); _lockedSince = 0;
    }
  }

  /* On any Klaviyo close/submit, release its lock (Klaviyo sometimes forgets to). */
  window.addEventListener('klaviyoForms', function (e) {
    var t = e && e.detail && e.detail.type;
    if (t === 'close' || t === 'closed' || t === 'submit' || t === 'redirectedToUrl') {
      setTimeout(function () { clearLocks(true); }, 60);
    }
  });

  /* First sign of scroll intent - finger-down, wheel, touch-drag, or a scroll key -
     clears the lock instantly, so there is no perceptible frozen moment. touchstart is
     the key one on mobile: it fires the instant the user touches the screen, before any
     drag even completes, guaranteeing the very first swipe attempt always works. */
  ['touchstart', 'wheel', 'touchmove'].forEach(function (ev) {
    window.addEventListener(ev, function () { clearLocks(true); }, { passive: true });
  });
  window.addEventListener('keydown', function (e) {
    if (/Arrow|PageUp|PageDown|Home|End| /.test(e.key || '')) clearLocks(true);
  }, { passive: true });

  /* The instant the cart drawer closes, drop any lingering lock - the reproduced
     freeze was: add to cart, Klaviyo silently sets its lock, you close the cart, and
     the page is stuck with no popup to dismiss. Watch the drawer's `active` class and
     react on the closing edge (plus the theme's panel:closed event as a backstop). */
  function watchDrawerClose() {
    var cd = document.getElementById('Cart-Drawer');
    if (!cd) return;
    var wasOpen = cd.classList.contains('active');
    new MutationObserver(function () {
      var open = cd.classList.contains('active');
      if (wasOpen && !open) { clearLocks(true); setTimeout(function () { clearLocks(true); }, 120); }
      wasOpen = open;
    }).observe(cd, { attributes: true, attributeFilter: ['class'] });
  }
  if (document.getElementById('Cart-Drawer')) watchDrawerClose();
  else document.addEventListener('DOMContentLoaded', watchDrawerClose);
  document.addEventListener('panel:closed', function () { setTimeout(function () { clearLocks(true); }, 60); });
  document.addEventListener('cart-drawer:close', function () { setTimeout(function () { clearLocks(true); }, 60); });

  /* Passive backup for the no-interaction case: a stray lock with no visible popup is
     cleared on the next 400ms tick; the 2s failsafe in clearLocks() means it can never
     linger even if popup detection is briefly wrong. */
  setInterval(function () { clearLocks(false); }, 400);
})();

/* ═══════════════════════════════════════════════════════════════════════════
   Cart-count reconciler
   Belt-and-suspenders accuracy: whatever changes the cart (add, remove, qty, gift
   sync, subscription add), re-read the real cart and write the true item_count into
   the header bubble. Fully isolated - only ever sets .thb-item-count text.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  var _timers = [];
  function syncCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var n = cart.item_count;
        document.querySelectorAll('#cart-drawer-toggle .thb-item-count, .thb-secondary-cart .thb-item-count').forEach(function (el) {
          el.textContent = n;
        });
      })
      .catch(function () {});
  }
  /* One settled re-check. The header bubble is already updated in real time by the
     cart drawer's own refresh (updateHeaderCount) and the PDP add handler, so this is
     only a backstop for cart changes that never open the drawer. A single late fire is
     enough and avoids the redundant /cart.js reads that used to pile up on every add. */
  function schedule() {
    _timers.forEach(clearTimeout);
    _timers = [1500].map(function (d) { return setTimeout(syncCount, d); });
  }

  ['cart:refresh', 'cart:item-added', 'cart:change', 'cart-drawer:open'].forEach(function (ev) {
    document.addEventListener(ev, schedule);
  });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) schedule(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncCount);
  } else {
    syncCount();
  }
})();
